import { z } from 'zod';
import { isDefined } from '../typeUtils/definedUtils.js';
/**
 * A comma-separated sort list, optionally prefixed per field with `-` for descending.
 *
 * The counterpart to {@link CursorPaginatedResponseSchema}: a house REST convention rather than any
 * one service's contract, so it lives beside the response envelope it pairs with.
 */
export const SORT_QUERY_PARAMETER_REGEX = /^(-?[a-z][A-Za-z0-9]*)(,-?[a-z][A-Za-z0-9]*)*$/;
const coerceQueryStringToNumber = (value) => {
    if (typeof value !== 'string' || value.trim() === '') {
        return value;
    }
    return Number(value);
};
const pageSizeSchema = z.preprocess(coerceQueryStringToNumber, z.number().min(1).max(500));
const pageFirstRequestSchema = z.object({ size: pageSizeSchema }).strict();
const pageAfterSchema = z
    .object({
    size: pageSizeSchema.optional().default(25),
    after: z.string().min(1),
})
    .strict();
/** The page parameter on its own, for a route that pages but has nothing to sort by. */
export const CursorPageSchema = z.union([pageFirstRequestSchema, pageAfterSchema]);
const sortSchema = z.string().regex(SORT_QUERY_PARAMETER_REGEX).optional();
/**
 * Adds optional cursor pagination and sort to a query schema, in the wire format Joi established:
 * a `page: { size, after }` object and a comma-separated `sort` string.
 *
 * One object with an optional `page`, rather than a union of paginated and unpaginated forms. A
 * query string is a flat set of parameters, and neither OpenAPI nor a generated client can express
 * "one of these two shapes": a union converts to `anyOf`, which publishes no parameters at all.
 *
 * Strictness is inherited rather than imposed, so extending a strict base stays strict and an
 * unknown key is rejected exactly where it was before.
 *
 * `sort` requires `page`, because every handler reads the sort out of the paginated branch and
 * ignores it otherwise: accepting a lone `sort` would answer 200 with an unsorted list.
 */
export function addOptionalCursorPaginationQuery(baseSchema) {
    return baseSchema
        .extend({ page: CursorPageSchema.optional(), sort: sortSchema })
        .check((context) => {
        const { page, sort } = context.value;
        if (isDefined(sort) && !isDefined(page)) {
            context.issues.push({
                code: 'custom',
                message: 'sort requires page',
                input: context.value,
                path: ['sort'],
            });
        }
    });
}
/** As {@link addOptionalCursorPaginationQuery}, for a handler that always paginates. */
export function addRequiredCursorPaginationQuery(baseSchema) {
    return baseSchema.extend({ page: CursorPageSchema, sort: sortSchema }).strict();
}
//# sourceMappingURL=cursorPaginationQuery.js.map